import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  type GetElementClassNameFn,
  IconNameEnum,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  Dialog,
  DialogContent,
  type DialogProps,
  DialogTrigger,
} from "@b2bportal/core-ui/overridable";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import styles from "./styles.module.scss";
import { AccountSelectors } from "@/modules/account";

export interface HowPricingWorksDialogProps extends DialogProps {
  className?: string;
}

const PricingCardContent = ({
  type,
  cn,
  cashPer100Points,
  addOnsCashPer100Points,
}: {
  type: "fixedPricing" | "flexPricing" | "creditCard";
  cn: GetElementClassNameFn;
  cashPer100Points: number;
  /**
   * Cash value of 100 points when redeemed against flight add-ons (seats,
   * CFAR). Only defined for Emerald cardholders; when undefined the "Add-ons"
   * section is omitted.
   */
  addOnsCashPer100Points?: number;
}) => {
  const { t, formatFiatCurrency, brand } = useI18nContext();
  const showAllIcons = type === "flexPricing" || type === "creditCard";
  const hasAddOns = addOnsCashPer100Points !== undefined;

  const title = useMemo(() => {
    switch (type) {
      case "fixedPricing":
        return t("core-flights.howPricingWorks.fixedPricing.title");
      case "flexPricing":
        return t("core-flights.howPricingWorks.flexPricing.title");
      case "creditCard":
        return t("core-flights.howPricingWorks.creditCard.title");
    }
  }, [type, t]);

  const whatText = useMemo(() => {
    switch (type) {
      case "fixedPricing":
        return t("core-flights.howPricingWorks.fixedPricing.what");
      case "flexPricing":
        return t("core-flights.howPricingWorks.flexPricing.what");
      case "creditCard":
        return t("core-flights.howPricingWorks.creditCard.what");
    }
  }, [type, t]);

  const baseFareText = useMemo(() => {
    switch (type) {
      case "fixedPricing":
        return t("core-flights.howPricingWorks.fixedPricing.baseFare");
      case "flexPricing":
        return t("core-flights.howPricingWorks.flexPricing.baseFare");
      case "creditCard":
        return t("core-flights.howPricingWorks.creditCard.baseFare");
    }
  }, [type, t]);

  const baseFareDescription = useMemo(() => {
    switch (type) {
      case "fixedPricing":
        return t(
          "core-flights.howPricingWorks.fixedPricing.baseFareDescription"
        );
      case "flexPricing":
        return t(
          "core-flights.howPricingWorks.flexPricing.baseFareDescription",
          {
            price: formatFiatCurrency({
              value: cashPer100Points,
              currencyCode: brand.currency.code,
            }),
            points: 100,
          }
        );
      case "creditCard":
        return t("core-flights.howPricingWorks.creditCard.baseFareDescription");
    }
  }, [type, t, formatFiatCurrency, cashPer100Points, brand.currency.code]);

  const taxesAndFeesText = useMemo(() => {
    switch (type) {
      case "fixedPricing":
        return t("core-flights.howPricingWorks.fixedPricing.taxesAndFees");
      case "flexPricing":
        return t("core-flights.howPricingWorks.flexPricing.taxesAndFees");
      case "creditCard":
        return t("core-flights.howPricingWorks.creditCard.taxesAndFees");
    }
  }, [type, t]);

  const taxesAndFeesDescription = useMemo(() => {
    switch (type) {
      case "fixedPricing":
        return t(
          "core-flights.howPricingWorks.fixedPricing.taxesAndFeesDescription"
        );
      case "flexPricing":
        return t(
          "core-flights.howPricingWorks.flexPricing.taxesAndFeesDescription",
          {
            price: formatFiatCurrency({
              value: cashPer100Points,
              currencyCode: brand.currency.code,
            }),
            points: 100,
          }
        );
      case "creditCard":
        return t(
          "core-flights.howPricingWorks.creditCard.taxesAndFeesDescription"
        );
    }
  }, [type, t, formatFiatCurrency, cashPer100Points, brand.currency.code]);

  const addOnsText = useMemo(() => {
    switch (type) {
      case "fixedPricing":
        return t("core-flights.howPricingWorks.fixedPricing.addOns");
      case "flexPricing":
        return t("core-flights.howPricingWorks.flexPricing.addOns");
      case "creditCard":
        return t("core-flights.howPricingWorks.creditCard.addOns");
    }
  }, [type, t]);

  const addOnsDescription = useMemo(() => {
    if (addOnsCashPer100Points === undefined) return undefined;

    const rate = {
      price: formatFiatCurrency({
        value: addOnsCashPer100Points,
        currencyCode: brand.currency.code,
      }),
      points: 100,
    };

    switch (type) {
      case "fixedPricing":
        return t(
          "core-flights.howPricingWorks.fixedPricing.addOnsDescription",
          rate
        );
      case "flexPricing":
        return t(
          "core-flights.howPricingWorks.flexPricing.addOnsDescription",
          rate
        );
      case "creditCard":
        return t("core-flights.howPricingWorks.creditCard.addOnsDescription");
    }
  }, [
    type,
    t,
    formatFiatCurrency,
    addOnsCashPer100Points,
    brand.currency.code,
  ]);

  const benefitText = useMemo(() => {
    switch (type) {
      case "fixedPricing":
        return t("core-flights.howPricingWorks.fixedPricing.benefit");
      case "flexPricing":
        return t("core-flights.howPricingWorks.flexPricing.benefit");
      case "creditCard":
        return t("core-flights.howPricingWorks.creditCard.benefit");
    }
  }, [type, t]);

  const benefitDescription = useMemo(() => {
    switch (type) {
      case "fixedPricing":
        return t(
          "core-flights.howPricingWorks.fixedPricing.benefitDescription"
        );
      case "flexPricing":
        return t("core-flights.howPricingWorks.flexPricing.benefitDescription");
      case "creditCard":
        return t("core-flights.howPricingWorks.creditCard.benefitDescription");
    }
  }, [type, t]);

  const exampleTitle = useMemo(() => {
    switch (type) {
      case "fixedPricing":
        return t("core-flights.howPricingWorks.fixedPricing.exampleTitle");
      case "flexPricing":
        return t("core-flights.howPricingWorks.flexPricing.exampleTitle");
      case "creditCard":
        return t("core-flights.howPricingWorks.creditCard.exampleTitle");
    }
  }, [type, t]);

  const exampleLocation = useMemo(() => {
    switch (type) {
      case "fixedPricing":
        return t("core-flights.howPricingWorks.fixedPricing.exampleLocation");
      case "flexPricing":
        return t("core-flights.howPricingWorks.flexPricing.exampleLocation");
      case "creditCard":
        return t("core-flights.howPricingWorks.creditCard.exampleLocation");
    }
  }, [type, t]);

  const exampleDetails = useMemo(() => {
    switch (type) {
      case "fixedPricing":
        return t("core-flights.howPricingWorks.fixedPricing.exampleDetails");
      case "flexPricing":
        return t("core-flights.howPricingWorks.flexPricing.exampleDetails");
      case "creditCard":
        return t("core-flights.howPricingWorks.creditCard.exampleDetails");
    }
  }, [type, t]);

  return (
    <div className={cn("card", { hasAddOns })}>
      <div className={cn("card-header")}>
        <h3 className={cn("card-title")}>{title}</h3>
      </div>

      <div className={cn("card-section")}>
        <div className={cn("icons")}>
          <div className={cn("icon")}>
            <Icon iconName={IconNameEnum.flights__active} />
          </div>
          {showAllIcons && (
            <>
              <div className={cn("icon")}>
                <Icon iconName={IconNameEnum.stays__active} />
              </div>
              <div className={cn("icon")}>
                <Icon iconName={IconNameEnum.cars__active} />
              </div>
              <div className={cn("icon")}>
                <Icon iconName={IconNameEnum.packages__active} />
              </div>
              <div className={cn("icon")}>
                <Icon iconName={IconNameEnum.activities__active} />
              </div>
            </>
          )}
        </div>
        <p className={cn("what-text")}>{whatText}</p>
      </div>

      <div className={cn("card-section")}>
        <h4 className={cn("section-heading")}>{baseFareText}</h4>
        <p className={cn("section-text")}>{baseFareDescription}</p>
      </div>

      <div className={cn("card-section")}>
        <h4 className={cn("section-heading")}>{taxesAndFeesText}</h4>
        <p className={cn("section-text")}>{taxesAndFeesDescription}</p>
      </div>

      {hasAddOns && (
        <div className={cn("card-section")}>
          <h4 className={cn("section-heading")}>{addOnsText}</h4>
          <p className={cn("section-text")}>{addOnsDescription}</p>
        </div>
      )}

      <div className={cn("card-section")}>
        <h4 className={cn("section-heading")}>{benefitText}</h4>
        <p className={cn("section-text")}>{benefitDescription}</p>
      </div>

      <div className={cn("example-box")}>
        <h5 className={cn("example-title")}>{exampleTitle}</h5>
        <div className={cn("example-location")}>
          <Icon iconName={IconNameEnum.mapPin} />
          <span>{exampleLocation}</span>
        </div>
        <p className={cn("example-details")}>{exampleDetails}</p>
      </div>
    </div>
  );
};

export const HowPricingWorksDialog = ({
  className,
  open,
  onClose,
  onOpen,
  children,
  ...props
}: HowPricingWorksDialogProps): JSX.Element => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "HowPricingWorksDialog");

  const basePointsToCashMultiplier = useSelector(
    AccountSelectors.getBasePointsToCashMultiplier
  );

  const cashPer100Points = basePointsToCashMultiplier
    ? 100 * basePointsToCashMultiplier
    : 1;

  // Emerald cardholders redeem flight add-ons (seats, CFAR) at the grid
  // overage/taxes rate rather than their base flex rate. Only they see the
  // "Add-ons" row.
  const isEmeraldAccount = useSelector(AccountSelectors.getIsEmeraldAccount);
  const gridOverageAndTaxesPointsToCashMultiplier = useSelector(
    AccountSelectors.getGridOverageAndTaxesPointsToCashMultiplier
  );
  const addOnsCashPer100Points =
    isEmeraldAccount && gridOverageAndTaxesPointsToCashMultiplier
      ? 100 * gridOverageAndTaxesPointsToCashMultiplier
      : undefined;

  return (
    <Dialog open={open} onClose={onClose} onOpen={onOpen} {...props}>
      <DialogTrigger>{children}</DialogTrigger>

      <DialogContent
        className={clsx(block, className)}
        title={t("core-flights.howPricingWorks.title")}
      >
        <p className={cn("intro")}>{t("core-flights.howPricingWorks.intro")}</p>

        <div className={cn("cards-container")}>
          <PricingCardContent
            type="fixedPricing"
            cn={cn}
            cashPer100Points={cashPer100Points}
            addOnsCashPer100Points={addOnsCashPer100Points}
          />
          <PricingCardContent
            type="flexPricing"
            cn={cn}
            cashPer100Points={cashPer100Points}
            addOnsCashPer100Points={addOnsCashPer100Points}
          />
          <PricingCardContent
            type="creditCard"
            cn={cn}
            cashPer100Points={cashPer100Points}
            addOnsCashPer100Points={addOnsCashPer100Points}
          />
        </div>

        <div className={cn("button-container")}>
          <button
            type="button"
            className={clsx(cn("cta-button"), "cta-button")}
            onClick={onClose}
          >
            {t("core-flights.howPricingWorks.close")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
